import { NO_IMAGE_AVAILABLE } from 'constants/index';
import _get from 'lodash/get';
import _isNumber from 'lodash/isNumber';

const getAlerternateImages = (source, destination, userBrand) => {
  Object.keys(source.media.alternate).map((key) => {
    const imageUrl = _get(source.media.alternate[key], 'medium.url') || NO_IMAGE_AVAILABLE[userBrand];
    return destination.push(/^\/{1}[a-zA-Z]/.test(imageUrl) ? `//neimanmarcus.com${imageUrl}` : imageUrl);
  });
};

export const getProductImage = (product = {}, skuInfo = {}, userBrand = '') => {
  const isDynamic = product.productFlags?.dynamicImageSkuColor;
  const productImg = _get(product, 'media.main.medium.url');
  const skuImg = _get(skuInfo, 'media.main.medium.url') || _get(skuInfo, 'media.main.thumbnail.url');
  const productImages = productImg ? [productImg] : [];
  const skuImages = skuImg ? [skuImg] : [productImg];
  const noImageAvailable = NO_IMAGE_AVAILABLE[userBrand];

  if (_get(product, 'media.alternate')) {
    getAlerternateImages(product, productImages, userBrand);
  }

  if (_get(skuInfo, 'media.alternate')) {
    getAlerternateImages(skuInfo, skuImages, userBrand);
  }

  const images = isDynamic ? skuImages || productImages : productImages || skuImages;

  if (!images.length) {
    images.push(noImageAvailable);
  }

  return images;
};

export const getProductVideo = (product = {}) => {
  const video = _get(product, 'media.video.video.url');
  if (video) {
    const thumbnail = _get(product, 'media.video.thumbnail.url');
    return {
      url: video,
      thumbnail,
    };
  }
  return null;
};

export const getCurrentCategoryFromTaxonomy = (id, array = []) => {
  const category = array.find((cat) => cat?.id === id);
  if (category) return category;
  const children = array.reduce((acc, cur) => acc.concat(cur?.children || []), []);
  return !children?.length ? children : getCurrentCategoryFromTaxonomy(id, children);
};

/**
 * Function to retrive object from URL search params
 * @param {String} [searchParam] "?text=search"
 * @returns {{[key: string]: string}} { text: "search" }
 */
export const getQueryParams = (searchParam) => {
  const queryParams = {};
  const newParams = new URLSearchParams(searchParam);

  newParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  return queryParams;
};

export const calculateDiscountPrice = (retailPrice, discount) => {
  let discountPrice;
  if (!discount) {
    discountPrice = retailPrice;
  } else {
    discountPrice = retailPrice * ((100 - discount) * 0.01);
  }

  return _isNumber(discountPrice) ? discountPrice.toFixed(2) : discountPrice;
};

export const cleanParams = (params) => {
  const newParams = {};
  Object.keys(params).forEach((key) => {
    newParams[key] = params[key] !== '' ? params[key] : null;
  });

  return newParams;
};

export const adjustSearchParams = (params) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    const returnStringValues = [
      'sortBy',
      'sortDirection',
      'divisionId',
      'groupId',
      'classId',
      'className',
      'hierarchyLevel1',
    ];
    if (value) {
      acc[key] = params[key];
    }

    if (key === 'text') {
      acc.search = value;
    }

    if (typeof value === 'string' && value !== '') {
      if (!returnStringValues.includes(key)) {
        acc[key] = value.split(',');
      }
      if (key === 'minPrice' || key === 'maxPrice') {
        acc[key] = parseInt(value, 10);
      }
      if (value === 'PRICE') {
        acc[key] = 'retailPrice';
      }
      if (value === 'CREATED') {
        acc[key] = 'sellableDate';
      }
      if (value === 'BESTRECOMMENDED') {
        acc[key] = 'bestRecommended';
      }
      if (key === 'recommendedBrand') {
        acc.brands = value?.split(',');
      }
      if (key === 'sizes' || key === 'sizeLabels') {
        acc[key] = value?.split('|');
      }
    }
    if (key === 'categoryId') {
      acc.categoryId = value;
    }

    delete acc.recommendedBrand;
    delete acc.text;

    return acc;
  }, {});
};
export const itemsCountTextForPLP = (totalCount, maxThreshold = 10000) =>
  totalCount > maxThreshold ? `${maxThreshold}+ items` : `${totalCount} items`;

export const mapSkuStoresToStores = (skuStores = [], webProductSku) => {
  const { storeInventories } = skuStores.find((skuStore) => skuStore.skuId === webProductSku) || {};
  const stores = storeInventories?.map(({ storeId, bopsQty, qty }) => ({
    storeId,
    bopsAvailQty: bopsQty,
    totalAvailQty: qty,
  }));

  return stores;
};

export const mapPimSkuToStoreInventory = (storeInventory, productPimSku) => {
  const { storeInventories } = storeInventory.find((skuStore) => skuStore.pimSku === productPimSku) || {};
  const stores = storeInventories?.map(({ storeId, bopsQty, qty }) => ({
    storeId,
    bopsAvailQty: bopsQty,
    totalAvailQty: qty,
  }));

  return stores;
};

export const extendSkuInfoWithProductDetails = (
  productDetails = {},
  skuInfo = {},
  { storeInventory = [], farFetchCheck = false, inventory = [] },
) => {
  let price = productDetails?.price?.retailPrice;
  let skuStores = [];
  const {
    id: webProductSku,
    productId: webProductId,
    dropShip: dropShipFlag,
    isRestricted = false,
    isPerishable = false,
    metadata,
  } = skuInfo;

  let stores;
  let stockLevel = skuInfo.stockLevel || 0;
  let paddedInventory = 0;
  let storeInventories = [];
  if (farFetchCheck) {
    stores = mapPimSkuToStoreInventory(storeInventory, metadata?.pimSkuId);
    skuStores = storeInventory || [];
    const storeInventoryDetails = storeInventory?.find((skuStore) => skuStore.pimSku === metadata?.pimSkuId) || {};
    const filterInventory = inventory.find((e) => e.pimSku === metadata.pimSkuId);
    paddedInventory = filterInventory ? filterInventory.quantity : 0;
    storeInventories = storeInventoryDetails?.storeInventories || [];
    stockLevel = filterInventory ? filterInventory.quantity : 0;
    price = skuInfo.retailPrice;
  } else {
    skuStores = productDetails.skuStores;
    stores = mapSkuStoresToStores(skuStores, webProductSku);
    const storeInventoryDetails = skuStores?.find((skuStore) => skuStore.skuId === webProductSku) || {};
    paddedInventory = storeInventoryDetails ? storeInventoryDetails?.paddedInventory : 0;
    storeInventories = storeInventoryDetails ? storeInventoryDetails?.storeInventories : [];
  }

  return {
    ...skuInfo,
    dropShip: Boolean(dropShipFlag === 'Y'),
    paddedInventory,
    price: { retailPrice: price },
    restrictedItem: isRestricted,
    isPerishable,
    skuStores,
    skuInventory: storeInventories,
    stockLevel,
    store: stores || [],
    webProductId,
    webProductSku,
  };
};

//Edit Styleboard Page
export const generateFilterParams = (activeFilters) => {
  const AreFiltersApplied = !!Object.values(activeFilters).flat().length;
  if (!AreFiltersApplied) return null;

  const filterParams = {};

  Object.keys(activeFilters).forEach((filterType) => {
    if (activeFilters[filterType].length) {
      filterParams[filterType] = activeFilters[filterType].join(',');
    }
  });

  return filterParams;
};

//Edit Styleboard Page
export const generateEditGalleryQueryParams = (params) => {
  const { page, pageSize, searchValue, categories, activeFilters, stylistId } = params;
  const activeFilterParams = generateFilterParams(activeFilters);

  return new URLSearchParams({
    edits: true,
    ...(categories !== 'MY_EDITS' ? { editsTypes: categories } : { stylistId, editsStatuses: 'APPROVED,PRIVATE' }),
    ...(pageSize && { pageSize }),
    ...(page && { page }),
    ...(searchValue && { text: searchValue }),
    ...(activeFilterParams && generateFilterParams(activeFilters)),
  }).toString();
};
